import { useState, useCallback, useEffect, useMemo } from 'react';
// lodash
import debounce from 'lodash/debounce';
// @mui
import { useTheme, alpha } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemButton from '@mui/material/ListItemButton';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useShortcut } from 'src/hooks/use-shortcut';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
// api
import { typesenseSearchCRMRecords } from 'src/api/typesense';
import { userTeamsGetAll } from 'src/api/user_teams';
import { recentRecordViewsGet } from 'src/api/recent_record_views';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
// context
import { useAuthContext } from 'src/auth/hooks';
// Utils
import { RESPONSE_STATUS_SUCCESS } from 'src/constant';
import { TYPESENSE_TOKEN_SEPARATOR } from 'src/constant';
import { formatPhoneNumber } from 'src/utils/phone-format';
import { findCRMValueByCRMKey } from 'src/utils/crm-commons';
// permissions
import { validPermissionToReadCRM } from 'src/security/permissions';
// config
import { APP_CONFIG } from 'src/config-global';

import ItemHighlight from './item-highlight';

// ----------------------------------------------------------------------

export default function Searchbar() {
  // Hooks
  const theme = useTheme();
  const openDialog = useBoolean();
  const mdUp = useResponsive('up', 'md');
  const router = useRouter();
  const { userSession } = useAuthContext();
  useShortcut('k', () => openDialog.onToggle());
  // States
  const [typesenseFiltersValue, setTypesenseFiltersValue] = useState('');
  const [typesenseDataValues, setTypesenseDataValues] = useState([]);
  const [loadingTypesenseFilters, setLoadingTypesenseFilters] = useState(false);
  const [showResultsNotFound, setShowResultsNotFound] = useState(false);
  const [showRecentRecordViews, setShowRecentRecordViews] = useState(true);
  const [recentRecordViewsValues, setRecentRecordViewsValues] = useState([]);

  const handleRedirectToAccount = useCallback((record_type, recordID, external, event) => {
    if(event) event.stopPropagation();
    const find = findCRMValueByCRMKey('value', record_type);
    if(external) {
      window.open(`${APP_CONFIG.app_main_domain}/dashboard/crm/${find.pluralLowerCase}/${recordID}/account`, '_blank');
    } else {
      openDialog.onFalse();
      router.push(paths.dashboard.crm.account(find ? find.pluralLowerCase : '', recordID));
    }
  }, []);

  const handleGetRecentRecordViews = useCallback(async () => {
    const getRecordViews = await recentRecordViewsGet();
    setRecentRecordViewsValues(getRecordViews.status === RESPONSE_STATUS_SUCCESS ? getRecordViews.data : []);
    setShowRecentRecordViews(getRecordViews.status === RESPONSE_STATUS_SUCCESS);
  }, []);

  const handleChangeTypesenseFilters = useMemo(() =>
    debounce(async (value) => {
      if(value) {
        setLoadingTypesenseFilters(true);
        const [ getTypesenseData, getUserTeams ] = await Promise.all([
          typesenseSearchCRMRecords(
            userSession.organization_id,
            'all',
            { pageSize: userSession.account_owner ? 50 : 100, page: 0 },
            value,
            'record_name,cell_phone_number',
            '',
            ''
          ),
          userTeamsGetAll(true),
        ]);
        const allDocuments = [];
        const allUserTeamsIds = getUserTeams.status === RESPONSE_STATUS_SUCCESS ? getUserTeams.data : [];
        if(getTypesenseData.status === RESPONSE_STATUS_SUCCESS) {
          for(const value of getTypesenseData.documents) {
            const find = findCRMValueByCRMKey('value', value.record_type);
            const getPermissionToRead = userSession.account_owner ? true : validPermissionToReadCRM(userSession.applied_permissions.crm[find.pluralLowerCase].view.type, userSession.applied_permissions.crm[find.pluralLowerCase].view.unassigned, userSession.id, value.owner_id, allUserTeamsIds);
            if(getPermissionToRead) {
              allDocuments.push(value);
            }
          }
        }
        setTypesenseDataValues(allDocuments);
        setShowResultsNotFound(getTypesenseData.status === RESPONSE_STATUS_SUCCESS && getTypesenseData.documents.length ? false : true);
        setLoadingTypesenseFilters(false);
        setShowRecentRecordViews(false);
      } else {
        setTypesenseDataValues([]);
        setShowResultsNotFound(false);
        setShowRecentRecordViews(true);
      }
    }, 500),
    []
  );

  useEffect(() => {
    handleChangeTypesenseFilters(typesenseFiltersValue);
  }, [typesenseFiltersValue]);

  useEffect(() => {
    if(!openDialog.value) {
      setTypesenseFiltersValue('');
      setTypesenseDataValues([]);
      setShowResultsNotFound(false);
    } else {
      handleGetRecentRecordViews();
    }
  }, [openDialog.value]);

  const renderItems = (
    <List disablePadding>
      {typesenseDataValues.map((record) => (
        <ListItemButton
          key={record.id}
          onClick={() => handleRedirectToAccount(record.record_type, record.id, false, null)}
          sx={{
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: 'transparent',
            borderBottomColor: (theme) => theme.palette.divider,
            '&:hover .launch-icon': {
              opacity: 1,
              pointerEvents: 'auto',
            },
            '&:hover': {
              borderRadius: 1,
              borderColor: (theme) => theme.palette.primary.main,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
            },
          }}
        >
          <ListItem>
            <ListItemText
              primary={<ItemHighlight query={typesenseFiltersValue} text={record.record_name} />}
              primaryTypographyProps={{
                typography: 'subtitle2',
                marginLeft: -3,
              }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={<ItemHighlight query={typesenseFiltersValue} text={record.cell_phone_number !== TYPESENSE_TOKEN_SEPARATOR ? formatPhoneNumber(record.cell_phone_number) : ''} />}
              primaryTypographyProps={{
                typography: 'subtitle2',
              }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <>
                <IconButton
                  className='launch-icon'
                  onClick={(event) => handleRedirectToAccount(record.record_type, record.id, true, event)}
                  sx={{
                    opacity: 0,
                    pointerEvents: 'none',
                    // transition: theme.transitions.create(['opacity'], {
                    //   duration: theme.transitions.duration.shorter,
                    // }),
                  }}
                >
                  <Iconify width={30} icon='gg:external' />
                </IconButton>
                <Chip variant='soft' color='success' size='small' label={findCRMValueByCRMKey('value', record.record_type).singular} />
                </>
              }
              primaryTypographyProps={{
                textAlign: 'right',
                marginRight: -3,
              }}
            />
          </ListItem>
        </ListItemButton>
      ))}
    </List>
  );

  const renderRecentRecordViews = (
    <List disablePadding>
      {recentRecordViewsValues.map((record) => (
        <ListItemButton
          key={record.id + record.record_data.id}
          onClick={() => handleRedirectToAccount(record.record_data.record_type, record.record_id, false, null)}
          sx={{
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: 'transparent',
            borderBottomColor: (theme) => theme.palette.divider,
            '&:hover .launch-icon': {
              opacity: 1,
              pointerEvents: 'auto',
            },
            '&:hover': {
              borderRadius: 1,
              borderColor: (theme) => theme.palette.primary.main,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
            },
          }}
        >
          <ListItem>
            <ListItemText
              primary={record.record_data.record_name}
              primaryTypographyProps={{
                typography: 'subtitle2',
                marginLeft: -3,
              }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={record.record_data.cell_phone_number !== TYPESENSE_TOKEN_SEPARATOR ? formatPhoneNumber(record.record_data.cell_phone_number) : ''}
              primaryTypographyProps={{
                typography: 'subtitle2',
              }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <>
                <IconButton
                  className='launch-icon'
                  onClick={(event) => handleRedirectToAccount(record.record_data.record_type, record.record_id, true, event)}
                  sx={{
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                >
                  <Iconify width={30} icon='gg:external' />
                </IconButton>
                <Chip variant='soft' color='success' size='small' label={findCRMValueByCRMKey('value', record.record_data.record_type).singular} />
                </>
              }
              primaryTypographyProps={{
                textAlign: 'right',
                marginRight: -3,
              }}
            />
          </ListItem>
        </ListItemButton>
      ))}
    </List>
  );

  const renderNotFound = (
    <Grid container mt={15}>
      <Grid item xs={12} textAlign='center'>
        <Typography variant='h6'>
          Not Found
        </Typography>
        <Typography variant='body2'>
          No results found for <strong>"{typesenseFiltersValue}"</strong>.
          <br />Try checking for typos or using complete words.
        </Typography>
      </Grid>
    </Grid>
  );

  const renderButton = (
    <Stack direction='row' alignItems='center'>
      <IconButton onClick={openDialog.onTrue}>
        <Iconify icon='eva:search-fill' />
      </IconButton>
      {mdUp && <Label sx={{ fontSize: 12, color: 'text.secondary' }}>⌘K</Label>}
    </Stack>
  );

  return (
    <>
      {renderButton}
      <Dialog fullWidth maxWidth='sm' open={openDialog.value} onClose={openDialog.onFalse}>
        <Grid container p={3} borderBottom={`solid 1px ${theme.palette.divider}`}>
          <Grid item xs={12}>
            <InputBase
              fullWidth
              autoFocus
              placeholder='Search...'
              value={typesenseFiltersValue}
              onChange={(event) => setTypesenseFiltersValue(event.target.value)}
              startAdornment={
                <InputAdornment position='start'>
                  <Iconify icon='eva:search-fill' width={24} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              endAdornment={
                loadingTypesenseFilters
                  ? <CircularProgress size={25} />
                  : <Label sx={{ letterSpacing: 1, color: 'text.secondary' }}>Esc</Label>
              }
              inputProps={{
                sx: { typography: 'h6' },
              }}
            />
          </Grid>
        </Grid>
        <Scrollbar sx={{ px: 3, pb: 3, height: 400 }}>
          {showRecentRecordViews && recentRecordViewsValues.length > 0 && (
            <Grid container>
              <Grid item xs={12} mt={2} mb={1}>
                <Typography variant='h6'>
                  Recently Opened
                </Typography>
              </Grid>
            </Grid>
          )}
          {showResultsNotFound ? renderNotFound : showRecentRecordViews ? renderRecentRecordViews : renderItems}
        </Scrollbar>
      </Dialog>
    </>
  );
}
